/**
 * Cup Red Box Detection Debug Tool
 * 
 * Tool này giúp validate và debug thuật toán detect red boxes:
 * - Visualize tất cả pixels màu đỏ
 * - Test nhiều thresholds khác nhau
 * - Hiển thị histogram RGB
 * - So sánh kết quả detect với ảnh gốc
 */

interface RedPixelInfo {
  x: number;
  y: number;
  r: number;
  g: number;
  b: number;
}

interface DetectionResult {
  threshold: string;
  boxes: any[];
  redPixelCount: number;
  redPixels: RedPixelInfo[]; // Sample of red pixels
}

// Các threshold khác nhau để test
const THRESHOLDS = {
  'STRICT (r>120, g<100, b<100)': (r: number, g: number, b: number) => r > 120 && g < 100 && b < 100,
  'VERY_STRICT (r>150, g<80, b<80)': (r: number, g: number, b: number) => r > 150 && g < 80 && b < 80,
  'MEDIUM (r>100, g<120, b<120, r>g, r>b)': (r: number, g: number, b: number) => r > 100 && r > g && r > b && g < 120 && b < 120,
  'PURE_RED (r>200, g<50, b<50)': (r: number, g: number, b: number) => r > 200 && g < 50 && b < 50,
  'RED_DOMINANT (r>g+50, r>b+50)': (r: number, g: number, b: number) => r > g + 50 && r > b + 50,
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Detect red boxes with a specific threshold function
 */
const detectRedBoxesWithThreshold = (
  canvas: HTMLCanvasElement,
  isRedFn: (r: number, g: number, b: number) => boolean
): { boxes: any[]; redPixels: RedPixelInfo[] } => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return { boxes: [], redPixels: [] };

  const width = canvas.width;
  const height = canvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const visited = new Uint8Array(width * height);
  const boxes: any[] = [];
  const redPixels: RedPixelInfo[] = [];

  // Collect red pixels for analysis
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      const r = data[idx];
      const g = data[idx + 1];
      const b = data[idx + 2];
      
      if (isRedFn(r, g, b)) {
        // Sample every 10th red pixel to avoid too much data
        if (redPixels.length < 1000 && Math.random() < 0.1) {
          redPixels.push({ x, y, r, g, b });
        }
      }
    }
  }

  // Flood fill to find connected regions
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x;
      const pixelIndex = index * 4;

      if (visited[index]) continue;

      const r = data[pixelIndex];
      const g = data[pixelIndex + 1];
      const b = data[pixelIndex + 2];

      if (isRedFn(r, g, b)) {
        let minX = x, maxX = x, minY = y, maxY = y;
        const stack = [index];
        visited[index] = 1;
        let count = 0;

        while (stack.length > 0) {
          const currIdx = stack.pop()!;
          const currX = currIdx % width;
          const currY = Math.floor(currIdx / width);

          count++;
          if (currX < minX) minX = currX;
          if (currX > maxX) maxX = currX;
          if (currY < minY) minY = currY;
          if (currY > maxY) maxY = currY;

          const neighbors = [currIdx - width, currIdx + width, currIdx - 1, currIdx + 1];

          for (const n of neighbors) {
            if (n < 0 || n >= width * height) continue;
            if (Math.abs((n % width) - currX) > 1) continue;

            const nr = data[n * 4];
            const ng = data[n * 4 + 1];
            const nb = data[n * 4 + 2];

            if (!visited[n] && isRedFn(nr, ng, nb)) {
              visited[n] = 1;
              stack.push(n);
            }
          }
        }

        if (count > 200 && (maxX - minX) > 10 && (maxY - minY) > 10) {
          boxes.push({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            area: (maxX - minX) * (maxY - minY),
            pixelCount: count,
            centerX: minX + (maxX - minX) / 2,
            centerY: minY + (maxY - minY) / 2,
          });
        }
      }
    }
  }

  return { boxes, redPixels };
};

/**
 * Create a visualization canvas showing red pixels
 */
const createRedPixelVisualization = (
  sourceCanvas: HTMLCanvasElement,
  redPixels: RedPixelInfo[],
  title: string
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = sourceCanvas.width;
  canvas.height = sourceCanvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Draw original image
  ctx.drawImage(sourceCanvas, 0, 0);

  // Overlay red pixels in bright green
  ctx.fillStyle = 'rgba(0, 255, 0, 0.8)';
  redPixels.forEach(pixel => {
    ctx.fillRect(pixel.x, pixel.y, 2, 2);
  });

  // Add title
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(5, 5, 300, 25);
  ctx.fillStyle = 'lime';
  ctx.font = 'bold 14px monospace';
  ctx.fillText(title, 10, 22);

  return canvas;
};

/**
 * Create a canvas showing detected boxes
 */
const createBoxVisualization = (
  sourceCanvas: HTMLCanvasElement,
  boxes: any[],
  title: string,
  color: string = 'cyan'
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = sourceCanvas.width;
  canvas.height = sourceCanvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Draw original image
  ctx.drawImage(sourceCanvas, 0, 0);

  // Draw boxes
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  boxes.forEach((box, idx) => {
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    
    // Draw box info
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(box.x + 2, box.y + 2, 150, 40);
    ctx.fillStyle = color;
    ctx.font = 'bold 11px monospace';
    ctx.fillText(`Box ${idx + 1}`, box.x + 5, box.y + 15);
    ctx.fillText(`${box.width}x${box.height}`, box.x + 5, box.y + 28);
    ctx.fillText(`Center: ${Math.round(box.centerX)},${Math.round(box.centerY)}`, box.x + 5, box.y + 40);

    // Draw center point
    ctx.fillStyle = 'red';
    ctx.beginPath();
    ctx.arc(box.centerX, box.centerY, 5, 0, Math.PI * 2);
    ctx.fill();
  });

  // Add title
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(5, 5, 300, 25);
  ctx.fillStyle = color;
  ctx.font = 'bold 14px monospace';
  ctx.fillText(title, 10, 22);

  return canvas;
};

/**
 * Run comprehensive detection debug
 */
export const debugCupDetection = async () => {
  console.log('🔍 Starting comprehensive cup detection debug...');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  const maskImageUrl = 'https://hi-huythanh--veritas-backend-api.modal.run/get_image?path=%2Ftemplates%2Fcoffee_cup%2Fly2.jpg';
  const cleanImageUrl = 'https://hi-huythanh--veritas-backend-api.modal.run/get_image?path=%2Ftemplates%2Fcoffee_cup%2Fly2-no-red-color.jpg';

  try {
    // Load images
    console.log('📂 Loading images...');
    const [maskImg, cleanImg] = await Promise.all([
      loadImage(maskImageUrl),
      loadImage(cleanImageUrl)
    ]);

    console.log(`✓ Mask image loaded: ${maskImg.width}x${maskImg.height}`);
    console.log(`✓ Clean image loaded: ${cleanImg.width}x${cleanImg.height}`);

    // Create canvas
    const maxSize = 1024;
    const scaleFactor = Math.min(maxSize / maskImg.width, maxSize / maskImg.height);
    const canvasWidth = Math.floor(maskImg.width * scaleFactor);
    const canvasHeight = Math.floor(maskImg.height * scaleFactor);

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Cannot get canvas context');

    ctx.drawImage(maskImg, 0, 0, canvas.width, canvas.height);
    console.log(`✓ Canvas created: ${canvas.width}x${canvas.height}`);

    // Test all thresholds
    console.log('\n🧪 Testing different thresholds...');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    const results: DetectionResult[] = [];

    for (const [name, isRedFn] of Object.entries(THRESHOLDS)) {
      console.log(`\n🎯 Testing: ${name}`);
      const { boxes, redPixels } = detectRedBoxesWithThreshold(canvas, isRedFn);
      
      console.log(`  • Red pixels found: ${redPixels.length * 10} (approx)`);
      console.log(`  • Boxes detected: ${boxes.length}`);
      
      if (boxes.length > 0) {
        boxes.forEach((box, idx) => {
          console.log(`  • Box ${idx + 1}: x=${box.x}, y=${box.y}, w=${box.width}, h=${box.height}`);
          console.log(`    Center: (${Math.round(box.centerX)}, ${Math.round(box.centerY)})`);
          console.log(`    Area: ${box.area}, Pixels: ${box.pixelCount}`);
        });
      } else {
        console.log('  ⚠️ No boxes detected!');
      }

      // Sample red pixel colors
      if (redPixels.length > 0) {
        const sample = redPixels.slice(0, 5);
        console.log(`  • Sample red pixels (first 5):`);
        sample.forEach(p => {
          console.log(`    (${p.x}, ${p.y}): RGB(${p.r}, ${p.g}, ${p.b})`);
        });
      }

      results.push({
        threshold: name,
        boxes,
        redPixelCount: redPixels.length * 10,
        redPixels: redPixels.slice(0, 100) // Keep first 100 for visualization
      });

      // Create visualizations
      const pixelViz = createRedPixelVisualization(canvas, redPixels, `${name} - Red Pixels`);
      pixelViz.style.border = '2px solid lime';
      pixelViz.style.margin = '10px';
      pixelViz.style.display = 'block';
      pixelViz.id = `debug-pixels-${name.replace(/\s+/g, '-')}`;
      document.body.appendChild(pixelViz);

      if (boxes.length > 0) {
        const boxViz = createBoxVisualization(canvas, boxes, `${name} - Detected Boxes`, 'cyan');
        boxViz.style.border = '2px solid cyan';
        boxViz.style.margin = '10px';
        boxViz.style.display = 'block';
        boxViz.id = `debug-boxes-${name.replace(/\s+/g, '-')}`;
        document.body.appendChild(boxViz);
      }
    }

    // Summary
    console.log('\n📊 SUMMARY');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    results.forEach(r => {
      console.log(`${r.threshold}:`);
      console.log(`  Red pixels: ${r.redPixelCount}, Boxes: ${r.boxes.length}`);
    });

    console.log('\n✅ Debug complete! Scroll down to see visualizations.');
    console.log('   • Green dots = detected red pixels');
    console.log('   • Cyan boxes = detected boundaries');

    return results;

  } catch (error) {
    console.error('❌ Debug failed:', error);
    throw error;
  }
};

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).debugCupDetection = debugCupDetection;
  console.log('🔧 Debug tool loaded! Run: debugCupDetection()');
}
